import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import {
  FaGithub,
  FaChevronLeft,
  FaChevronRight,
  FaApple,
  FaGooglePlay,
  FaGlobe,
  FaTimes,
  FaArrowRight,
  FaSearch,
  FaPlus,
  FaCheck,
} from "react-icons/fa";
import {
  categories,
  toneStyle,
  selectProjects,
  techIndex,
} from "../data/projects";
import { useSite, SORTS, scrollToSection } from "../SiteContext";
import { useI18n } from "../i18n";
import { useContent } from "../i18n/useContent";
import { formatPeriod } from "../i18n/format";
import Architecture from "./Architecture";
import "./Projects.css";

const ALL_TECHS = techIndex();

// Helper function to get initials from project title
const getInitials = (title) => {
  if (!title) return "HT";
  // Ignore separators like "-" and "&" so "Jaride - Multi-Service" gives "JM"
  const words = title
    .split(" ")
    .map((word) => word.replace(/[^a-zA-Z0-9]/g, ""))
    .filter((word) => word.length > 0);
  if (words.length === 0) return "HT";
  if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};

// Placeholder component for projects without images
const ProjectPlaceholder = ({ title, className = "" }) => (
  <div className={`project-placeholder ${className}`}>
    <span className="placeholder-initials">{getInitials(title)}</span>
  </div>
);

// Project image that falls back to the initials placeholder when the file
// is missing or fails to load
const ProjectImage = ({ src, alt, title }) => {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  if (!src || failed) {
    return <ProjectPlaceholder title={title} />;
  }

  return (
    <img
      className="project-img"
      src={src}
      alt={alt}
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
    />
  );
};

// Full-size image inside the detail modal, with the same placeholder fallback
const ModalImage = ({ src, alt, title }) => {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  if (failed) {
    return <ProjectPlaceholder title={title} className="modal-placeholder" />;
  }

  return (
    <img src={src} alt={alt} className="modal-main-image" onError={() => setFailed(true)} />
  );
};

// Grid layout class for the featured collage, keyed on how many images exist
const getGridClass = (count) => {
  switch (count) {
    case 1:
      return "grid-single";
    case 2:
      return "grid-double";
    case 3:
      return "grid-triple";
    case 4:
      return "grid-quad";
    default:
      return "grid-multiple";
  }
};

const pad = (n) => String(n).padStart(2, "0");

// Stack names are the site's cross-reference: click one anywhere and the grid
// narrows to the work it was used on. Selected chips read as selected.
const TechChip = ({ tech, onSelect, active, t }) => (
  <button
    type="button"
    className={`chip chip-btn ${active ? "is-active" : ""}`}
    onClick={() => onSelect(tech)}
    aria-pressed={active}
    title={t(active ? 'a11y.stopFilteringByTech' : 'a11y.filterByTech', { tech })}
  >
    {tech}
  </button>
);

const Projects = () => {
  const {
    selectedProject,
    projectTab,
    setProjectTab,
    openProject,
    closeProject,
    query,
    setQuery,
    categoryFilter,
    setCategoryFilter,
    techFilters,
    toggleTech,
    sort,
    setSort,
    hasFilters,
    clearFilters,
  } = useSite();
  const { lang, t } = useI18n();
  const { projects, architectures } = useContent();

  const [currentProject, setCurrentProject] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [stackOpen, setStackOpen] = useState(false);

  // The record for whatever is open, and the tab it should be showing. A
  // project with no architecture can never land on the diagram tab. The tab
  // itself lives only in SiteContext — a local mirror here used to drift out
  // of sync with it (see Projects.test.js for the bug that produced).
  const selectedArchitecture = selectedProject
    ? architectures[selectedProject.id]
    : undefined;
  const activeTab = selectedArchitecture ? projectTab : "shots";

  const filteredProjects = selectProjects(
    { query, category: categoryFilter, techs: techFilters, sort },
    projects,
    lang
  );

  // Counts shown against each category reflect the filters already applied, so
  // a reader can see which options still lead somewhere before clicking.
  const categoryCounts = categories.reduce((acc, category) => {
    acc[category.key] = selectProjects(
      { query, category: category.key, techs: techFilters, sort },
      projects,
      lang
    ).length;
    return acc;
  }, {});

  const featuredProjects = projects.filter((project) => project.featured);
  const featured = featuredProjects[currentProject];

  const navigateToProject = useCallback(
    (direction) => {
      setCurrentProject((prev) => {
        if (direction === "next") return prev === featuredProjects.length - 1 ? 0 : prev + 1;
        return prev === 0 ? featuredProjects.length - 1 : prev - 1;
      });
    },
    [featuredProjects.length]
  );

  const openProjectModal = (project) => {
    setCurrentImageIndex(0);
    openProject(project);
  };

  const closeProjectModal = useCallback(() => {
    setCurrentImageIndex(0);
    closeProject();
  }, [closeProject]);

  const navigateImage = useCallback(
    (direction) => {
      const total = selectedProject?.images?.length;
      if (!total) return;
      setCurrentImageIndex((prev) =>
        direction === "next" ? (prev + 1) % total : (prev - 1 + total) % total
      );
    },
    [selectedProject]
  );

  // Keyboard navigation for modal
  useEffect(() => {
    if (!selectedProject) return undefined;

    const handleKeyPress = (e) => {
      if (e.key === "Escape") closeProjectModal();
      if (activeTab !== "shots") return;
      if (e.key === "ArrowLeft") navigateImage("prev");
      if (e.key === "ArrowRight") navigateImage("next");
    };

    document.addEventListener("keydown", handleKeyPress);
    return () => document.removeEventListener("keydown", handleKeyPress);
  }, [selectedProject, closeProjectModal, navigateImage, activeTab]);

  // The modal covers the grid, so filtering from inside it has to dismiss the
  // modal — otherwise the reader changes a filter and sees nothing happen.
  const toggleTechFromModal = (tech) => {
    toggleTech(tech);
    closeProjectModal();
    requestAnimationFrame(() => scrollToSection("projects-filter"));
  };

  // Swipe on the featured spread. Pointer events cover touch and trackpad drag
  // without a library; a 48px threshold keeps a tap from registering as a flick.
  const swipeStart = useRef(null);

  const onPointerDown = (e) => {
    swipeStart.current = { x: e.clientX, y: e.clientY };
  };

  const onPointerUp = (e) => {
    if (!swipeStart.current) return;
    const dx = e.clientX - swipeStart.current.x;
    const dy = e.clientY - swipeStart.current.y;
    swipeStart.current = null;
    // A pointer event without real coordinates (some environments never fill
    // in clientX/clientY) turns dx into NaN. Every comparison with NaN is
    // false, so both the distance and the vertical-swipe checks below would
    // fall through instead of guarding, and a plain tap would register as a
    // swipe to the previous project.
    if (!Number.isFinite(dx) || !Number.isFinite(dy) || Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy))
      return;
    navigateToProject(dx < 0 ? "next" : "prev");
  };

  const onCarouselKeyDown = (e) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      navigateToProject("prev");
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      navigateToProject("next");
    }
  };

  const renderProjectLinks = (links) => {
    if (!links) return null;

    const items = [
      { key: "website", href: links.website, icon: <FaGlobe />, label: t('projects.linkWebsite') },
      { key: "appStore", href: links.appStore, icon: <FaApple />, label: t('projects.linkAppStore') },
      { key: "playStore", href: links.playStore, icon: <FaGooglePlay />, label: t('projects.linkPlayStore') },
      { key: "github", href: links.github, icon: <FaGithub />, label: t('projects.linkSource') },
    ].filter((item) => item.href);

    if (items.length === 0) return null;

    return (
      <div className="project-links">
        {items.map((item) => (
          <a
            key={item.key}
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            className="project-link"
          >
            {item.icon}
            <span>{item.label}</span>
          </a>
        ))}
      </div>
    );
  };

  const renderCollage = (project) => {
    const images = project?.images || [];
    const visible = images.slice(0, 4);
    const hidden = images.length - visible.length;

    if (visible.length === 0) {
      return (
        <div className="images-grid grid-single">
          <div className="image-item">
            <ProjectPlaceholder title={project?.title} />
          </div>
        </div>
      );
    }

    return (
      <div className={`images-grid ${getGridClass(visible.length)}`}>
        {visible.map((image, index) => (
          <div key={image} className={`image-item item-${index + 1}`}>
            <ProjectImage
              src={image}
              alt={t('a11y.screenshotAltNumbered', { title: project?.title, index: index + 1 })}
              title={project?.title}
            />
            {hidden > 0 && index === visible.length - 1 && (
              <span className="overlay-count">+{hidden}</span>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <section id="projects" className="projects">
      <div className="container">
        <header className="masthead reveal">
          <div className="masthead-rule">
            <span className="masthead-label">{t('projects.label')}</span>
            <span className="masthead-note">{t('projects.count', { count: projects.length })}</span>
          </div>
          <h2 className="masthead-title">{t('projects.title')}</h2>
          <p className="masthead-lede">{t('projects.lede')}</p>
        </header>

        {/* Featured spread */}
        {featured && (
          <div
            className="featured reveal"
            role="group"
            aria-roledescription="carousel"
            aria-label={t('a11y.featuredCarousel')}
            tabIndex={0}
            onKeyDown={onCarouselKeyDown}
            onPointerDown={onPointerDown}
            onPointerUp={onPointerUp}
          >
            <div className="featured-bar">
              <span className="featured-label">{t('projects.featured')}</span>
              <div className="featured-nav">
                <button
                  className="step-btn"
                  onClick={() => navigateToProject("prev")}
                  aria-label={t('a11y.prevFeatured')}
                >
                  <FaChevronLeft />
                </button>
                <span className="featured-count">
                  {pad(currentProject + 1)} <span aria-hidden="true">/</span>{" "}
                  {pad(featuredProjects.length)}
                </span>
                <button
                  className="step-btn"
                  onClick={() => navigateToProject("next")}
                  aria-label={t('a11y.nextFeatured')}
                >
                  <FaChevronRight />
                </button>
              </div>
            </div>

            <article className="featured-spread" style={toneStyle(featured.category)}>
              <button
                className="featured-media"
                onClick={() => openProjectModal(featured)}
                aria-label={t('projects.viewDetails', { title: featured.title })}
              >
                {renderCollage(featured)}
              </button>

              <div className="featured-body">
                <p className="featured-type">
                  {featured.type}
                  {architectures[featured.id] && (
                    <span className="card-arch-flag">{t('projects.architecture')}</span>
                  )}
                </p>
                <h3 className="featured-title">{featured.title}</h3>
                <p className="featured-period">{formatPeriod(featured, lang, t)}</p>

                <p className="featured-description">{featured.description}</p>

                {/* Two highlights and six chips, not everything: the row is as
                    tall as this column, and an exhaustive list here forced the
                    screenshot to sit in a frame half full of white. The rest is
                    a click away under "Full details". */}
                <ul className="featured-benefits">
                  {featured.benefits.slice(0, 2).map((benefit, index) => (
                    <li key={index}>{benefit}</li>
                  ))}
                </ul>

                <div className="chip-row">
                  {featured.technologies.slice(0, 6).map((tech) => (
                    <TechChip
                      key={tech}
                      tech={tech}
                      onSelect={toggleTech}
                      active={techFilters.includes(tech)}
                      t={t}
                    />
                  ))}
                  {featured.technologies.length > 6 && (
                    <span className="chip chip-more">
                      +{featured.technologies.length - 6}
                    </span>
                  )}
                </div>

                <div className="featured-footer">
                  {renderProjectLinks(featured.links)}
                  <button
                    className="link-quiet"
                    onClick={() => openProjectModal(featured)}
                  >
                    {t('projects.fullDetails')}
                    <FaArrowRight />
                  </button>
                </div>
              </div>
            </article>
          </div>
        )}

        {/* Filter panel */}
        <div className="filters reveal" id="projects-filter">
          <div className="filters-top">
            <div className="search-field">
              <FaSearch aria-hidden="true" />
              <input
                type="search"
                className="search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('projects.searchPlaceholder')}
                aria-label={t('a11y.searchProjects')}
              />
              {query && (
                <button
                  type="button"
                  className="search-clear"
                  onClick={() => setQuery("")}
                  aria-label={t('a11y.clearSearch')}
                >
                  <FaTimes />
                </button>
              )}
            </div>

            <div className="sort-control">
              <span className="filters-label" id="sort-label">{t('projects.sort')}</span>
              <div className="sort-options" role="group" aria-labelledby="sort-label">
                {SORTS.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    className={`sort-btn ${sort === option.key ? "active" : ""}`}
                    onClick={() => setSort(option.key)}
                    aria-pressed={sort === option.key}
                  >
                    {t(option.labelKey)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="filters-row">
            <span className="filters-label">{t('projects.type')}</span>
            <div className="filter-list">
              {categories.map((category) => {
                const count = categoryCounts[category.key];
                return (
                  <button
                    key={category.key}
                    className={`filter-btn ${categoryFilter === category.key ? "active" : ""}`}
                    style={toneStyle(category.key)}
                    onClick={() => setCategoryFilter(category.key)}
                    disabled={count === 0 && categoryFilter !== category.key}
                  >
                    {t(category.labelKey)}
                    <span className="filter-count">{count}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="filters-row">
            <span className="filters-label">{t('projects.stack')}</span>
            <div className="stack-area">
              <div className="stack-selected">
                {techFilters.map((tech) => (
                  <span className="tech-pill" key={tech}>
                    <span className="tech-pill-label">{tech}</span>
                    <button
                      type="button"
                      className="tech-pill-clear"
                      onClick={() => toggleTech(tech)}
                      aria-label={t('a11y.stopFilteringByTech', { tech })}
                    >
                      <FaTimes />
                    </button>
                  </span>
                ))}

                <button
                  type="button"
                  className={`stack-toggle ${stackOpen ? "is-open" : ""}`}
                  onClick={() => setStackOpen((open) => !open)}
                  aria-expanded={stackOpen}
                >
                  {stackOpen ? <FaTimes /> : <FaPlus />}
                  {stackOpen
                    ? t('projects.stackClose')
                    : techFilters.length > 0
                    ? t('projects.stackAddAnother')
                    : t('projects.stackChoose')}
                </button>
              </div>

              {stackOpen && (
                <div className="stack-picker">
                  {ALL_TECHS.map(({ name, count }) => {
                    const active = techFilters.includes(name);
                    return (
                      <button
                        key={name}
                        type="button"
                        className={`stack-option ${active ? "is-active" : ""}`}
                        onClick={() => toggleTech(name)}
                        aria-pressed={active}
                      >
                        <span className="stack-option-check" aria-hidden="true">
                          {active && <FaCheck />}
                        </span>
                        <span className="stack-option-name">{name}</span>
                        <span className="stack-option-count">{count}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="filters-foot">
            <span className="filters-count" aria-live="polite">
              {t('projects.resultCount', {
                count: filteredProjects.length,
                display: pad(filteredProjects.length),
              })}
            </span>
            {hasFilters && (
              <button type="button" className="filters-clear" onClick={clearFilters}>
                <FaTimes aria-hidden="true" />
                {t('projects.clearAll')}
              </button>
            )}
          </div>
        </div>

        {/* All projects */}
        {filteredProjects.length === 0 && (
          <div className="projects-empty">
            <p className="projects-empty-title">{t('projects.empty')}</p>
            <p className="projects-empty-hint">
              {techFilters.length > 1
                ? t('projects.emptyHintTech')
                : t('projects.emptyHintSearch')}
            </p>
            <button type="button" className="btn btn-ghost" onClick={clearFilters}>
              {t('projects.clearAllFilters')}
            </button>
          </div>
        )}

        <div className="projects-grid">
          {filteredProjects.map((project, index) => (
            <article
              key={project.id}
              className="project-card reveal"
              style={{ ...toneStyle(project.category), '--i': index % 6 }}
            >
              <button
                className="card-media"
                onClick={() => openProjectModal(project)}
                aria-label={t('projects.viewDetails', { title: project.title })}
              >
                <ProjectImage
                  src={project.images?.[0]}
                  alt={t('a11y.screenshotAlt', { title: project.title })}
                  title={project.title}
                />
              </button>

              <div className="card-body">
                <p className="card-type">
                  {project.type}
                  {architectures[project.id] && (
                    <span className="card-arch-flag">{t('projects.architecture')}</span>
                  )}
                </p>
                <h3 className="card-title">{project.title}</h3>
                <p className="card-period">{formatPeriod(project, lang, t)}</p>

                <p className="card-description">{project.description}</p>

                <div className="chip-row card-chips">
                  {project.technologies.slice(0, 3).map((tech) => (
                    <TechChip
                      key={tech}
                      tech={tech}
                      onSelect={toggleTech}
                      active={techFilters.includes(tech)}
                      t={t}
                    />
                  ))}
                  {project.technologies.length > 3 && (
                    <span className="chip chip-more">
                      +{project.technologies.length - 3}
                    </span>
                  )}
                </div>

                <div className="card-footer">
                  {renderProjectLinks(project.links)}
                  <button
                    className="card-details"
                    onClick={() => openProjectModal(project)}
                  >
                    {t('projects.details')}
                    <FaArrowRight />
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Project detail modal.
            Rendered through a portal: the enclosing .container establishes a
            stacking context, which would otherwise trap the overlay beneath
            the fixed site header */}
        {selectedProject &&
          createPortal(
            <div className="project-modal-overlay" onClick={closeProjectModal}>
              <div
                className="project-modal"
                style={toneStyle(selectedProject.category)}
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label={selectedProject.title}
              >
                <button
                  className="modal-close-btn"
                  onClick={closeProjectModal}
                  aria-label={t('a11y.close')}
                >
                  <FaTimes />
                </button>

                <div className={`modal-content ${activeTab === "arch" ? "is-arch" : ""}`}>
                  <div className="modal-image-section">
                    {selectedArchitecture && (
                      <div className="media-tabs" aria-label={t('a11y.projectMedia')}>
                        <button
                          type="button"
                          className={`media-tab ${activeTab === "shots" ? "is-active" : ""}`}
                          aria-pressed={activeTab === "shots"}
                          onClick={() => setProjectTab("shots")}
                        >
                          {t('projects.screenshots')}
                        </button>
                        <button
                          type="button"
                          className={`media-tab ${activeTab === "arch" ? "is-active" : ""}`}
                          aria-pressed={activeTab === "arch"}
                          onClick={() => setProjectTab("arch")}
                        >
                          {t('projects.architecture')}
                        </button>
                      </div>
                    )}

                    {activeTab === "arch" ? (
                      <div className="arch-frame">
                        <Architecture architecture={selectedArchitecture} />
                      </div>
                    ) : selectedProject.images && selectedProject.images.length > 0 ? (
                      <>
                        <div className="modal-stage">
                          <ModalImage
                            src={selectedProject.images[currentImageIndex]}
                            alt={t('a11y.imageAlt', {
                              title: selectedProject.title,
                              index: currentImageIndex + 1,
                              total: selectedProject.images.length,
                            })}
                            title={selectedProject.title}
                          />

                          {selectedProject.images.length > 1 && (
                            <>
                              <button
                                className="modal-nav-btn prev"
                                onClick={() => navigateImage("prev")}
                                aria-label={t('a11y.prevImage')}
                              >
                                <FaChevronLeft />
                              </button>
                              <button
                                className="modal-nav-btn next"
                                onClick={() => navigateImage("next")}
                                aria-label={t('a11y.nextImage')}
                              >
                                <FaChevronRight />
                              </button>
                              <span className="modal-image-counter">
                                {pad(currentImageIndex + 1)} / {pad(selectedProject.images.length)}
                              </span>
                            </>
                          )}
                        </div>

                        {selectedProject.images.length > 1 && (
                          <div className="modal-thumbnails">
                            {selectedProject.images.map((img, idx) => (
                              <button
                                key={img}
                                className={`modal-thumbnail ${currentImageIndex === idx ? "active" : ""}`}
                                onClick={() => setCurrentImageIndex(idx)}
                                aria-label={t('a11y.showImage', { index: idx + 1 })}
                              >
                                <img
                                  src={img}
                                  alt=""
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                  }}
                                />
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="modal-stage">
                        <ProjectPlaceholder
                          title={selectedProject.title}
                          className="modal-placeholder"
                        />
                      </div>
                    )}
                  </div>

                  <div className="modal-details-section">
                    <div className="modal-header">
                      <p className="modal-type">{selectedProject.type}</p>
                      <h2 className="modal-title">{selectedProject.title}</h2>
                      <p className="modal-period">{formatPeriod(selectedProject, lang, t)}</p>
                    </div>

                    <p className="modal-description">{selectedProject.description}</p>

                    {selectedProject.benefits?.length > 0 && (
                      <div className="modal-block">
                        <h4 className="modal-block-title">{t('projects.highlights')}</h4>
                        <ul className="modal-benefits">
                          {selectedProject.benefits.map((benefit, idx) => (
                            <li key={idx}>{benefit}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="modal-block">
                      <h4 className="modal-block-title">{t('projects.builtWith')}</h4>
                      <div className="chip-row">
                        {selectedProject.technologies.map((tech) => (
                          <TechChip
                            key={tech}
                            tech={tech}
                            onSelect={toggleTechFromModal}
                            active={techFilters.includes(tech)}
                            t={t}
                          />
                        ))}
                      </div>
                    </div>

                    {renderProjectLinks(selectedProject.links)}
                  </div>
                </div>
              </div>
            </div>,
            document.body
          )}
      </div>
    </section>
  );
};

export default Projects;
